const STORAGE_KEY = 'exa_dashboard_pane_layout_v1';

const DEFAULT_WIDTHS = Object.freeze({
  sidebar: 260,
  details: 340,
});

const MIN_WIDTHS = Object.freeze({
  sidebar: 180,
  details: 260,
  center: 480,
});

const MAX_WIDTHS = Object.freeze({
  sidebar: 600,
  details: 720,
});

const FALLBACK_RESIZER_TOTAL_WIDTH = 20;

const toNumber = (value, fallback) => {
  const parsed = Number.parseFloat(String(value ?? '').trim());
  return Number.isFinite(parsed) ? parsed : fallback;
};

const toPositiveInt = (value, fallback) => {
  const parsed = toNumber(value, fallback);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return fallback;
  }
  return Math.round(parsed);
};

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

export const createPaneLayoutState = ({ container }) => {
  const getContainerWidth = () => {
    const width = toNumber(container?.getBoundingClientRect?.().width, 0);
    if (width > 0) {
      return width;
    }
    return Math.max(window.innerWidth || 0, 0);
  };

  const getResizerTotalWidth = () => {
    if (!container) {
      return FALLBACK_RESIZER_TOTAL_WIDTH;
    }

    const total = Array.from(container.querySelectorAll('[data-pane-resizer]')).reduce(
      (sum, element) => {
        const width = toNumber(element.getBoundingClientRect?.().width, 0);
        return sum + Math.max(0, width);
      },
      0
    );

    return total > 0 ? total : FALLBACK_RESIZER_TOTAL_WIDTH;
  };

  const fitToViewport = (layout) => {
    const containerWidth = getContainerWidth();
    const resizerWidth = getResizerTotalWidth();

    let sidebar = clamp(
      Math.round(toNumber(layout?.sidebar, DEFAULT_WIDTHS.sidebar)),
      MIN_WIDTHS.sidebar,
      MAX_WIDTHS.sidebar
    );
    let details = clamp(
      Math.round(toNumber(layout?.details, DEFAULT_WIDTHS.details)),
      MIN_WIDTHS.details,
      MAX_WIDTHS.details
    );

    let centerWidth = containerWidth - resizerWidth - sidebar - details;
    if (centerWidth >= MIN_WIDTHS.center) {
      return {
        sidebar,
        details,
      };
    }

    let shortage = MIN_WIDTHS.center - centerWidth;

    const reduceSidebar = Math.min(sidebar - MIN_WIDTHS.sidebar, Math.ceil(shortage / 2));
    sidebar -= reduceSidebar;
    shortage -= reduceSidebar;

    const reduceDetails = Math.min(details - MIN_WIDTHS.details, shortage);
    details -= reduceDetails;
    shortage -= reduceDetails;

    if (shortage > 0) {
      const extraSidebar = Math.min(sidebar - MIN_WIDTHS.sidebar, shortage);
      sidebar -= extraSidebar;
      shortage -= extraSidebar;
    }

    if (shortage > 0) {
      const extraDetails = Math.min(details - MIN_WIDTHS.details, shortage);
      details -= extraDetails;
    }

    return {
      sidebar,
      details,
    };
  };

  const applyLayout = (layout) => {
    if (!container) {
      return {
        sidebar: DEFAULT_WIDTHS.sidebar,
        details: DEFAULT_WIDTHS.details,
      };
    }

    const normalized = fitToViewport(layout);
    container.style.setProperty('--dashboard-sidebar-width', `${normalized.sidebar}px`);
    container.style.setProperty('--dashboard-details-width', `${normalized.details}px`);
    return normalized;
  };

  const clearLayoutVariables = () => {
    if (!container) {
      return;
    }
    container.style.removeProperty('--dashboard-sidebar-width');
    container.style.removeProperty('--dashboard-details-width');
  };

  const readAppliedLayout = () => {
    if (!container) {
      return { ...DEFAULT_WIDTHS };
    }

    const styles = window.getComputedStyle(container);
    const sidebar = toPositiveInt(
      styles.getPropertyValue('--dashboard-sidebar-width'),
      DEFAULT_WIDTHS.sidebar
    );
    const details = toPositiveInt(
      styles.getPropertyValue('--dashboard-details-width'),
      DEFAULT_WIDTHS.details
    );

    return {
      sidebar,
      details,
    };
  };

  const readStorage = () => {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) {
        return null;
      }

      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') {
        return null;
      }

      return {
        sidebar: toPositiveInt(parsed.sidebar, DEFAULT_WIDTHS.sidebar),
        details: toPositiveInt(parsed.details, DEFAULT_WIDTHS.details),
      };
    } catch (error) {
      return null;
    }
  };

  const writeStorage = (layout) => {
    try {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          sidebar: toPositiveInt(layout?.sidebar, DEFAULT_WIDTHS.sidebar),
          details: toPositiveInt(layout?.details, DEFAULT_WIDTHS.details),
        })
      );
    } catch (error) {
      // ignore storage errors
    }
  };

  const loadInitialLayout = () => {
    const stored = readStorage();
    const initial = stored || DEFAULT_WIDTHS;
    return applyLayout(initial);
  };

  const updateSidebar = (sidebarWidth, { persist = false } = {}) => {
    const current = readAppliedLayout();
    const next = applyLayout({
      sidebar: sidebarWidth,
      details: current.details,
    });

    if (persist) {
      writeStorage(next);
    }
    return next;
  };

  const updateDetails = (detailsWidth, { persist = false } = {}) => {
    const current = readAppliedLayout();
    const next = applyLayout({
      sidebar: current.sidebar,
      details: detailsWidth,
    });

    if (persist) {
      writeStorage(next);
    }
    return next;
  };

  const persistCurrent = () => {
    writeStorage(readAppliedLayout());
  };

  const syncToViewport = () => {
    applyLayout(readAppliedLayout());
  };

  return {
    loadInitialLayout,
    syncToViewport,
    clearLayoutVariables,
    updateSidebar,
    updateDetails,
    persistCurrent,
  };
};
